import { useState } from 'react';
import { useSelector } from 'react-redux';
import {
  Bar,
  Brush,
  ComposedChart,
  LabelList,
  Legend,
  Line,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { IChartProps, StickyTooltip } from '../../../../../component/charts';
import ChartCard from '../../../../../component/charts/chartCard';
import {
  labelFormatterMillions,
  labelFormatterMoneyAsInteger,
} from '../../../../../shared/formatters/formatters';
import { ReleasesSalesEcoMapChartData } from './types';

export const LaunchedUnitsSalesECOMAP: React.FC = () => {
  const { chartsDataPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  const { requestStatus } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  return (
    <ChartCard
      data={chartsDataPrimary['eco_map']}
      Chart={LaunchedUnitsSalesECOMAPChart}
      cy={'LaunchedUnitsSalesECOMAP'}
      id={'launchedUnitsSalesECOMAP'}
      requestStatus={requestStatus['launchedUnits']}
    />
  );
};

const LaunchedUnitsSalesECOMAPChart: React.FC<
  IChartProps<ReleasesSalesEcoMapChartData[]>
> = ({ data, cy, id }) => {
  data = data?.map((el: any) => ({
    ...el,
    total_sales: Number(el.total_sales / 1000000),
  }));
  const type = '_' + data[0]['type' as keyof (typeof data)[0]];

  const [hasClicked, setHasClicked] = useState<boolean>(false);
  const [recordTooltip, setRecordTooltip] = useState<JSX.Element | null>(null);
  const [stopChange, setStopChange] = useState<boolean>(false);
  const formatterFunctions = [labelFormatterMillions, labelFormatterMillions];

  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      data-cy={`LaunchedUnitsSales${cy}`}
    >
      <ComposedChart
        data={data}
        id={id}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
        onClick={() => setHasClicked(!hasClicked)}
      >
        <Tooltip
          wrapperStyle={{ visibility: 'visible' }}
          content={
            <StickyTooltip
              hasClicked={hasClicked}
              recordTooltip={recordTooltip}
              setRecordTooltip={setRecordTooltip}
              stopChange={stopChange}
              setStopChange={setStopChange}
              formatterFunctions={formatterFunctions}
            />
          }
        />
        <XAxis dataKey="period" />
        <Legend iconType="circle" />
        <YAxis />
        <Bar
          isAnimationActive={false}
          dataKey="total_sales"
          name="Lançamentos em VGV"
          fill="#8884d8"
        >
          <LabelList
            dataKey="total_sales"
            position="top"
            formatter={labelFormatterMoneyAsInteger}
          />
        </Bar>
        <Line
          isAnimationActive={false}
          name="Média lançamentos em VGV"
          type="monotone"
          dataKey={`total_sales_average${type}`}
          stroke="#ff0000"
        />
        <Brush dataKey="period" height={30} stroke="#8884d8" />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export const LaunchedUnitsSalesECO: React.FC = () => {
  const { chartsDataPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  const { requestStatus } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  return (
    <ChartCard
      data={chartsDataPrimary['eco_map']}
      Chart={LaunchedUnitsSalesECOChart}
      cy={'LaunchedUnitsSalesECO'}
      id={'launchedUnitsSalesECO'}
      requestStatus={requestStatus['launchedUnits']}
    />
  );
};

const LaunchedUnitsSalesECOChart: React.FC<
  IChartProps<ReleasesSalesEcoMapChartData[]>
> = ({ data, cy, id }) => {
  data = data?.map((el: any) => ({
    ...el,
    eco_sales_value: Number(el.eco_sales_value / 1000000),
  }));
  const type = '_' + data[0]['type' as keyof (typeof data)[0]];
  const average = Number(
    data[0][`eco_sales_average${type}` as keyof (typeof data)[0]]
  );

  const [hasClicked, setHasClicked] = useState<boolean>(false);
  const [recordTooltip, setRecordTooltip] = useState<JSX.Element | null>(null);
  const [stopChange, setStopChange] = useState<boolean>(false);
  const formatterFunctions = [labelFormatterMillions, labelFormatterMillions];
  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      data-cy={`LaunchedUnitsSales${cy}`}
    >
      <ComposedChart
        data={data}
        id={id}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
        onClick={() => setHasClicked(!hasClicked)}
      >
        <Tooltip
          wrapperStyle={{ visibility: 'visible' }}
          content={
            <StickyTooltip
              hasClicked={hasClicked}
              recordTooltip={recordTooltip}
              setRecordTooltip={setRecordTooltip}
              stopChange={stopChange}
              setStopChange={setStopChange}
              formatterFunctions={formatterFunctions}
            />
          }
        />
        <XAxis dataKey="period" />
        <YAxis />
        <Bar
          isAnimationActive={false}
          dataKey="eco_sales_value"
          name="ECO"
          fill="#8884d8"
        >
          <LabelList
            dataKey="eco_sales_value"
            position="top"
            formatter={labelFormatterMoneyAsInteger}
          />
        </Bar>
        <Line
          isAnimationActive={false}
          name="Média lançamentos em VGV (ECO)"
          dataKey={`eco_sales_average${type}`}
          stroke="red"
        />
        <Brush dataKey="period" height={30} stroke="#8884d8" />
        <Legend iconType="circle" />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export const LaunchedUnitsSalesMAP: React.FC = () => {
  const { chartsDataPrimary } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  const { requestStatus } = useSelector(
    (state: any) => state.offersPrimaryState
  );
  return (
    <ChartCard
      data={chartsDataPrimary['eco_map']}
      Chart={LaunchedUnitsSalesMAPChart}
      cy={'LaunchedUnitsSalesMAP'}
      id={'launchedUnitsSalesMAP'}
      requestStatus={requestStatus['launchedUnits']}
    />
  );
};

const LaunchedUnitsSalesMAPChart: React.FC<
  IChartProps<ReleasesSalesEcoMapChartData[]>
> = ({ data, cy, id }) => {
  data = data?.map((el: any) => ({
    ...el,
    map_sales_value: Number(el.map_sales_value / 1000000),
  }));
  const type = '_' + data[0]['type' as keyof (typeof data)[0]];
  const average = Number(
    data[0][`map_sales_average${type}` as keyof (typeof data)[0]]
  );
  const [hasClicked, setHasClicked] = useState<boolean>(false);
  const [recordTooltip, setRecordTooltip] = useState<JSX.Element | null>(null);
  const [stopChange, setStopChange] = useState<boolean>(false);
  const formatterFunctions = [labelFormatterMillions, labelFormatterMillions];
  return (
    <ResponsiveContainer
      width="100%"
      height="100%"
      data-cy={`LaunchedUnitsSales${cy}`}
    >
      <ComposedChart
        data={data}
        id={id}
        margin={{
          top: 20,
          right: 30,
          left: 20,
          bottom: 5,
        }}
        onClick={() => setHasClicked(!hasClicked)}
      >
        <Tooltip
          wrapperStyle={{ visibility: 'visible' }}
          content={
            <StickyTooltip
              hasClicked={hasClicked}
              recordTooltip={recordTooltip}
              setRecordTooltip={setRecordTooltip}
              stopChange={stopChange}
              setStopChange={setStopChange}
              formatterFunctions={formatterFunctions}
            />
          }
        />
        <XAxis dataKey="period" />
        <YAxis />
        <Bar
          isAnimationActive={false}
          dataKey="map_sales_value"
          name="MAP"
          fill="#8884d8"
        >
          <LabelList
            dataKey="map_sales_value"
            position="top"
            formatter={labelFormatterMoneyAsInteger}
          />
        </Bar>
        <Line
          isAnimationActive={false}
          name="Média lançamentos em VGV (MAP)"
          dataKey={`eco_sales_average${type}`}
          stroke="red"
        />
        <Legend iconType="circle" />
        <Brush dataKey="period" height={30} stroke="#8884d8" />
      </ComposedChart>
    </ResponsiveContainer>
  );
};
